import { ApolloError } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { NOTIFICATION_TIMEOUT_LONG_8S } from '@modules/common/constants';
import { IntegrationsActionType } from '@modules/integrationsV2/store/integrationsActionType';
import { useIntegrationsStore } from '@modules/integrationsV2/store/integrationsStore';
import { getSuccessMessageForAddOrEditIntegration } from '@modules/integrationsV2/utils';
import {
  GetTenantByIdDocument,
  IntegrationChannelType,
  IntegrationDirection,
  IntegrationInputData,
  IntegrationType,
  useCreateIntegrationMutation,
} from 'generated/graphql';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';

interface CreateIntegrationEdgeServerProps {
  tenantID: string;
  direction: IntegrationDirection;
  channel: IntegrationChannelType;
  integrationType: IntegrationType;
  createdByUser: string;
  inputData: IntegrationInputData;
  onError?: (error: ApolloError) => void;
  onCompleted?: () => void;
  description?: string;
}

const useCreateIntegrationEdgeServer = ({
  tenantID,
  direction,
  channel,
  integrationType,
  createdByUser,
  inputData,
  description,
  onError,
  onCompleted,
}: CreateIntegrationEdgeServerProps) => {
  const { showErrorNotification, showSuccessNotification } =
    useNotificationContext();

  const isAddIntegrationDrawerOpen = useIntegrationsStore(
    ({ view }) => view.isAddDrawerOpen,
  );
  const dispatch = useIntegrationsStore(({ dispatch }) => dispatch);

  const [createIntegrationMutation, { loading, error, called }] =
    useCreateIntegrationMutation({
      variables: {
        tenantID,
        direction,
        channel,
        createdByUser,
        data: inputData,
        integrationType,
        integrationDescription: description
          ? {
              description,
            }
          : undefined,
      },
      onError: (error) => {
        if (onError) {
          onError(error);
          return;
        }
        showErrorNotification({
          contentComponent: `Something went wrong${
            error.message ? `: ${error.message}` : '!'
          }`,
          props: {},
        });
      },
      onCompleted: (data) => {
        onCompleted?.();

        const integrationID = data?.createIntegration?.id;

        if (isAddIntegrationDrawerOpen) {
          dispatch({
            type: IntegrationsActionType.SET_VIEW,
            payload: {
              isAddDrawerOpen: false,
            },
          });
        }

        if (direction === IntegrationDirection.Inbound) {
          dispatch({
            type: IntegrationsActionType.SET_VIEW,
            payload: {
              selectedIntegrationSourceOption: {
                integrationID: integrationID,
                integrationType,
                channelType: channel,
              },
            },
          });
        }

        const successMessage =
          channel && integrationType
            ? getSuccessMessageForAddOrEditIntegration({
                channelType: channel,
                integrationType,
                action: 'add',
              })
            : null;

        if (successMessage && direction === IntegrationDirection.Inbound) {
          showSuccessNotification({
            contentComponent: () => successMessage,
            props: {
              isClosable: true,
              timeout: NOTIFICATION_TIMEOUT_LONG_8S,
            },
          });
        }
      },
      refetchQueries: [
        {
          query: GetTenantByIdDocument,
          variables: {
            tenantID,
          },
        },
      ],
      awaitRefetchQueries: true,
    });

  const status = apolloStatusToQueryStatus(loading, called, false, error);

  return {
    status,
    error,
    createIntegration: createIntegrationMutation,
  };
};

export default useCreateIntegrationEdgeServer;
